/**
 * Generated manually from OpenAPI (BPMN Workspace 0.2.0) — API v2.
 * Source: `/Users/loziuk/work/bpmn_python/openapi.json` (`/api/v2/*`)
 *
 * Backend sends DateTime in UTC. Client maps response fields to the user's
 * local timezone (ISO 8601 with offset). Request DateTime fields are converted
 * back to UTC before send.
 */

export type UUID = string;
/** ISO 8601 datetime in the user's local timezone after API response mapping. */
export type DateTime = string;

/** Known DateTime JSON keys from the OpenAPI models below. */
const DATE_TIME_KEYS = new Set(['created_at', 'updated_at', 'expires_at', 'archived_at']);

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Treat naive ISO datetimes as UTC (backend convention). */
function parseUtcInstant(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const d = new Date(hasOffset ? trimmed : `${trimmed}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWithOffset(d: Date, offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const oh = pad2(Math.floor(abs / 60));
  const om = pad2(abs % 60);
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}` +
    `${sign}${oh}:${om}`
  );
}

/** UTC (or naive-UTC) ISO string → local timezone ISO with offset. */
export function toLocalDateTime(value: DateTime): DateTime;
export function toLocalDateTime(value: DateTime | null | undefined): DateTime | null | undefined;
export function toLocalDateTime(value: DateTime | null | undefined): DateTime | null | undefined {
  if (value == null) return value;
  const d = parseUtcInstant(value);
  if (!d) return value;
  return formatWithOffset(d, -d.getTimezoneOffset());
}

/** Local (or any) ISO string → UTC ISO with `Z`. */
export function toUtcDateTime(value: DateTime): DateTime;
export function toUtcDateTime(value: DateTime | null | undefined): DateTime | null | undefined;
export function toUtcDateTime(value: DateTime | null | undefined): DateTime | null | undefined {
  if (value == null) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
}

function mapDateTimeTree(data: unknown, convert: (v: string) => string): unknown {
  if (data == null || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data.map((item) => mapDateTimeTree(item, convert));
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(data as Record<string, unknown>)) {
    if (DATE_TIME_KEYS.has(key) && typeof val === 'string') {
      out[key] = convert(val);
    } else if (val != null && typeof val === 'object') {
      out[key] = mapDateTimeTree(val, convert);
    } else {
      out[key] = val;
    }
  }
  return out;
}

/** Recursively remap DateTime fields on API responses to local timezone. */
export function mapDateTimesToLocal<T>(data: T): T {
  return mapDateTimeTree(data, (v) => toLocalDateTime(v)) as T;
}

/** Recursively remap DateTime fields on request bodies to UTC. */
export function mapDateTimesToUtc<T>(data: T): T {
  return mapDateTimeTree(data, (v) => toUtcDateTime(v)) as T;
}

export type GlobalRole = 'super_admin' | 'admin' | 'user';

/** Account role. Project roles are separate. */
export type OrgRole = 'admin' | 'user';

export type User = {
  id: UUID;
  created_at: DateTime;
  email?: string | null;
  full_name?: string | null;
  avatar_url?: string | null;
  is_active?: boolean | null;
  global_role?: GlobalRole | null;
  org_role?: OrgRole | null;
  has_password?: boolean;
  has_google?: boolean;
  totp_enabled?: boolean;
  email_2fa_enabled?: boolean;
  passkeys?: PasskeyInfo[];
  is_system_admin?: boolean;
  is_org_admin?: boolean;
  can_create_project?: boolean;
  can_manage_mfa?: boolean;
  can_edit_org_profile?: boolean;
  /** Seed, organization admin, or owner of at least one project. */
  can_archive?: boolean;
};

export type Organization = {
  name: string;
  description?: string | null;
  is_active?: boolean;
  has_logo: boolean;
  /** False until an admin saves the organization profile. */
  initialized: boolean;
  updated_at?: DateTime | null;
  allow_totp: boolean;
  allow_email_otp: boolean;
  allow_passkey: boolean;
  /** Password sign-in requires a second factor after an admin saves an enabled method. */
  password_mfa_required?: boolean;
  /** Suggested sites for an iframe. Empty means the dialog starts with no sites checked. */
  embed_origins?: string[];
};

export type OrganizationUpdate = {
  name: string;
  allow_totp: boolean;
  allow_email_otp: boolean;
  allow_passkey: boolean;
  current_password?: string;
  description?: string | null;
  is_active?: boolean;
  embed_origins?: string[];
};

export type OrgMember = {
  id: UUID;
  email?: string | null;
  full_name?: string | null;
  global_role?: GlobalRole | null;
  org_role?: OrgRole | null;
  is_active: boolean;
  is_license_seed: boolean;
  /** True while the person has not finished the invite link. */
  invitation_pending?: boolean;
  created_at: DateTime;
};

export type MemberInviteResult = {
  status: string;
  email: string;
  full_name: string;
  email_sent: boolean;
};

/** GET /license/notice — any signed-in user. */
export type LicenseNotice = {
  status: string;
  expires_at?: DateTime | null;
  /** True once the license has ended and writes are blocked. */
  read_only: boolean;
  /** Whole days until `expires_at`, or null when the date is unknown. */
  days_left: number | null;
};

export type LicenseOverview = {
  license_type: string;
  status: string;
  expires_at?: DateTime | null;
  users_used: number;
  users_available: number;
  projects_used: number;
  projects_available: number;
  /** Active share links and iframes already in use. Null when the count is unknown. */
  shared_links_used?: number | null;
  /** Share-link limit. Null when the license does not cap links. */
  shared_links_available?: number | null;
};

export type PasskeyInfo = {
  id: UUID;
  name?: string | null;
  created_at: DateTime;
};

/**
 * Signed-in session. Access and refresh tokens are HttpOnly cookies on the API
 * host and are not present in this payload.
 */
export type AuthSession = {
  token_type: string;
  expires_in: number;
  csrf_token: string;
  user: User;
  sharing_enabled?: boolean;
  share_public_base_url?: string;
};

export type CsrfToken = {
  csrf_token: string;
};

export type RefreshResult = {
  token_type: string;
  expires_in: number;
  csrf_token: string;
};

export type GoogleAuthRequest = {
  id_token: string;
};

/** GET /auth/providers — which sign-in methods the current license allows. */
export type AuthProviders = {
  google: boolean;
  password: boolean;
};

export type PasswordLoginRequest = {
  email: string;
  password: string;
};

export type MfaMethod = 'totp' | 'email';

/** POST /auth/login and POST /auth/google — second factor, or authenticator enrollment. */
export type LoginChallenge = {
  mfa_required: boolean;
  enrollment_required?: boolean;
  mfa_token?: string | null;
  methods?: string[];
  token_type?: string;
  expires_in?: number;
  csrf_token?: string;
  user?: User;
};

export type LoginMfaRequest = {
  mfa_token: string;
  method: MfaMethod;
  code: string;
};

export type MfaTokenRequest = {
  mfa_token: string;
};

export type WebAuthnLoginVerifyRequest = {
  mfa_token: string;
  credential: Record<string, unknown>;
};

/** GET /auth/register/{token} — invite link before an account exists. */
export type RegistrationPreview = {
  email: string;
  full_name?: string | null;
  expires_at: DateTime;
  password_set: boolean;
  allow_totp?: boolean;
  allow_email_otp?: boolean;
  allow_passkey?: boolean;
  org_role?: 'admin' | 'user';
};

/** POST /auth/register/{token}/2fa/totp/start */
export type TotpStartResponse = {
  secret: string;
  otpauth_uri: string;
};

export type WebAuthnRegisterVerifyRequest = {
  credential: Record<string, unknown>;
  name?: string | null;
};

export type Project = {
  id: UUID;
  owner_id: UUID | null;
  name: string;
  description?: string | null;
  is_archived: boolean;
  archived_at?: DateTime | null;
  created_at: DateTime;
  updated_at: DateTime;
  /** False when the current role can only view this project. */
  can_write?: boolean;
  can_manage_archive?: boolean;
  can_edit_settings?: boolean;
  can_manage_members?: boolean;
  can_assign_owner?: boolean;
  can_archive_project?: boolean;
  can_restore_project?: boolean;
  can_delete_project?: boolean;
  can_delete_folder?: boolean;
  can_archive_diagram?: boolean;
  can_move?: boolean;
  can_share?: boolean;
  can_export?: boolean;
  /** False when the caller can open only folders shared with them. */
  all_folders?: boolean;
  owner_name?: string | null;
  /** Display names of every member with the owner role. */
  owner_names?: string[];
};

export type ProjectCreate = {
  name: string;
  description?: string | null;
};

export type ProjectUpdate = {
  name?: string | null;
  description?: string | null;
  is_archived?: boolean | null;
};

/** OpenAPI `ProjectRole` */
export enum ProjectMemberRole {
  owner = 'owner',
  editor = 'editor',
  viewer = 'viewer',
}

export type ProjectRole = ProjectMemberRole;

/** Тіло POST /projects/{id}/members (`ProjectMemberAdd`) */
export type ProjectMemberAdd = {
  email: string;
  role: ProjectMemberRole;
  full_name?: string;
};

/** Тіло PUT /projects/{id}/members. Потрібне хоча б одне з `role` або `full_name`. */
export type ProjectMemberUpdate = {
  email: string;
  role?: ProjectMemberRole;
  full_name?: string;
};

/** @deprecated Use `ProjectMemberAdd` */
export type ProjectMemberInvite = ProjectMemberAdd;

/**
 * Елемент списку GET /projects/{id}/members.
 * Pending invite (email never logged in): `user_id` is `null`.
 */
export type ProjectMember = {
  id: UUID;
  user_id: UUID | null;
  role: ProjectMemberRole;
  created_at: DateTime;
  email?: string | null;
  full_name?: string | null;
  /** False when the person can open only the folders shared with them. */
  all_folders?: boolean;
};

export type UserSummary = {
  id: UUID;
  email: string;
  full_name?: string | null;
  avatar_url?: string | null;
};

export type DiagramSortBy = 'name' | 'created_at' | 'updated_at' | 'archived_at';
export type ArchiveListStatus = 'active' | 'archived' | 'all';
export type ProjectSortBy = 'name' | 'created_at' | 'updated_at';
export type SortOrder = 'asc' | 'desc';

/** OpenAPI `PaginationMeta` for paginated list endpoints. */
export type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

/** OpenAPI `PaginatedResponse`. */
export type PaginatedResponse<T> = {
  data: T[];
  pagination: PaginationMeta;
};

export type Folder = {
  id: UUID;
  project_id: UUID;
  name: string;
  parent_folder_id?: UUID | null;
  created_at: DateTime;
  is_archived: boolean;
  archived_at?: DateTime | null;
};

export type FolderCreate = {
  project_id: UUID;
  name: string;
  parent_folder_id?: UUID | null;
};

export type FolderUpdate = {
  name?: string | null;
  is_archived?: boolean | null;
};

/** OpenAPI `DiagramResponse` (v2 diagram payload). */
export type Diagram = {
  id: UUID;
  project_id: UUID;
  name: string;
  description?: string | null;
  folder_id?: UUID | null;
  current_xml: string;
  created_by_id?: UUID | null;
  updated_by_id?: UUID | null;
  created_by?: UserSummary | null;
  updated_by?: UserSummary | null;
  is_archived: boolean;
  archived_at?: DateTime | null;
  /** True when this diagram, its folder, or its project is archived. */
  in_archive?: boolean;
  created_at: DateTime;
  updated_at: DateTime;
  /** False when the current role can only view this diagram. */
  can_write?: boolean;
  can_share?: boolean;
  can_export?: boolean;
  can_move?: boolean;
  can_archive_diagram?: boolean;
};

/** Alias matching OpenAPI schema name. */
export type DiagramResponse = Diagram;

export type DiagramCreate = {
  project_id: UUID;
  name: string;
  description?: string | null;
  folder_id?: UUID | null;
  xml?: string | null;
};

export type DiagramLinkRef = {
  source_diagram_id: UUID;
  element_id: string;
  target_diagram_id: UUID;
};

export type DiagramLinksResponse = {
  outgoing: DiagramLinkRef[];
  incoming: DiagramLinkRef[];
};

export type DiagramUpdate = {
  name?: string | null;
  description?: string | null;
  folder_id?: UUID | null;
  xml?: string | null;
  comment?: string | null;
  is_archived?: boolean | null;
};

export type AIDiagramRequest = {
  prompt: string;
  current_xml?: string | null;
  diagram_id?: UUID | null;
};

export type AIDiagramResponse = {
  xml: string;
  explanation?: string | null;
};

export type Version = {
  id: UUID;
  diagram_id: UUID;
  version_number: number;
  xml: string;
  comment?: string | null;
  created_by_id: UUID;
  created_at: DateTime;
};

export type ShareKind = 'link' | 'embed';

export type ShareLink = {
  token: string;
  kind: ShareKind;
  name: string;
  expires_at?: DateTime | null;
  allowed_origins?: string[];
  version_number?: number | null;
  public_url: string;
  embed_html?: string | null;
  is_active: boolean;
};

export type ShareList = {
  shares: ShareLink[];
  used: number;
  max: number;
};

export type ShareCreate = {
  diagram_id: UUID;
  kind?: ShareKind;
  expires_at?: DateTime | null;
  allowed_origins?: string[] | null;
  version_id?: UUID | null;
};

export type ShareUpdate = {
  diagram_id: UUID;
  refresh?: boolean;
  version_id?: UUID | null;
  expires_at?: DateTime | null;
  allowed_origins?: string[] | null;
};

export type ShareRefresh = {
  diagram_id: UUID;
};

export type ShareRevokeResponse = {
  status: string;
  revoked_count: number;
};

export type LogoutResponse = {
  status: string;
};

/** OpenAPI `SearchEntityType` — query param `type` on GET /search/. */
export type SearchEntityType = 'project' | 'diagram';

/** OpenAPI `SearchResult` — GET /search/ hit (active projects/diagrams only). */
export type SearchResult = {
  id: UUID;
  type: SearchEntityType;
  name: string;
  description?: string | null;
  /** Set for diagrams; null for projects. */
  project_id?: UUID | null;
  is_archived: boolean;
  updated_at: DateTime;
  can_write?: boolean;
};

